import api from '../config/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

/**
 * Procurement Service - API client for procurement management
 * Handles suppliers, purchase orders, goods receipts, invoices, payments, and reports
 */

// ============================================================================
// SUPPLIERS
// ============================================================================

export const getSuppliers = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/suppliers`, { params });
  return response.data;
};

export const getSupplier = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/suppliers/${id}`);
  return response.data;
};

export const createSupplier = async (data) => {
  const response = await api.post(`${API_BASE}/api/procurement/suppliers`, data);
  return response.data;
};

export const updateSupplier = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/procurement/suppliers/${id}`, data);
  return response.data;
};

export const deactivateSupplier = async (id) => {
  const response = await api.delete(`${API_BASE}/api/procurement/suppliers/${id}`);
  return response.data;
};

// Hard-delete — backend returns 409 if any PO references the supplier.
export const deleteSupplierPermanent = async (id) => {
  const response = await api.delete(`${API_BASE}/api/procurement/suppliers/${id}/permanent`);
  return response.data;
};

export const getSupplierPerformance = async (id, params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/suppliers/${id}/performance`, { params });
  return response.data;
};

// Per-supplier bank-payment statement: actual bank transactions + open-PO obligations.
export const getSupplierBankStatement = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/supplier-bank-payments/${id}`);
  return response.data;
};

export const addSupplierProduct = async (supplierId, data) => {
  const response = await api.post(`${API_BASE}/api/procurement/suppliers/${supplierId}/products`, data);
  return response.data;
};

export const updateSupplierProduct = async (supplierId, productId, data) => {
  const response = await api.put(`${API_BASE}/api/procurement/suppliers/${supplierId}/products/${productId}`, data);
  return response.data;
};

// Payment milestones (NetSuite-style multi-tranche payment terms).
// listMilestones returns { data, meta: { milestone_types, trigger_events } }.
// replaceMilestones is full-replace — sends the entire array, backend deactivates
// any milestones not in the new list.
export const listSupplierPaymentMilestones = async (supplierId) => {
  const response = await api.get(`${API_BASE}/api/procurement/suppliers/${supplierId}/payment-terms`);
  return response.data;
};

export const replaceSupplierPaymentMilestones = async (supplierId, milestones) => {
  const response = await api.put(`${API_BASE}/api/procurement/suppliers/${supplierId}/payment-terms`, { milestones });
  return response.data;
};

// Bulk import — body { rows: [...] }, returns { inserted, failed, errors[] }
export const bulkImportSuppliers = async (rows) => {
  const response = await api.post(`${API_BASE}/api/procurement/suppliers/bulk-import`, { rows });
  return response.data;
};

// Bulk update — body { updates: [{ id, ...changes }, ...] }
export const bulkUpdateSuppliers = async (updates) => {
  const response = await api.post(`${API_BASE}/api/procurement/suppliers/bulk-update`, { updates });
  return response.data;
};

// CSV export — triggers a file download in the browser. Honours the same
// filters as getSuppliers. Streams as text/csv with content-disposition.
export const exportSuppliersCsv = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/suppliers/export.csv`, {
    params,
    responseType: 'blob',
  });
  // Derive a filename: prefer Content-Disposition, fall back to date-stamped.
  let filename = `suppliers-${new Date().toISOString().slice(0, 10)}.csv`;
  const cd = response.headers?.['content-disposition'];
  if (cd) {
    const m = cd.match(/filename="?([^"]+)"?/);
    if (m) filename = m[1];
  }
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  return { success: true, filename };
};

// ============================================================================
// PURCHASE ORDERS
// ============================================================================

export const getPurchaseOrders = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/purchase-orders`, { params });
  return response.data;
};

export const getPurchaseOrder = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/purchase-orders/${id}`);
  return response.data;
};

// Manufacture POs: per-component readiness at the manufacturer warehouse
// (required per production BOM vs available vs on-order vs shortfall).
export const getComponentReadiness = async (poId) => {
  const response = await api.get(`${API_BASE}/api/procurement/purchase-orders/${poId}/component-readiness`);
  return response.data;
};

// Manufacture POs: backflush preview for one PO line (BOM × qty per component,
// availability + FIFO cost estimate) — feeds the receive dialog's actuals grid.
export const getBackflushPreview = async (poId, poItemId, quantity, receiptDate = null) => {
  const response = await api.get(`${API_BASE}/api/procurement/purchase-orders/${poId}/backflush-preview`, {
    params: { po_item_id: poItemId, quantity, ...(receiptDate ? { receipt_date: receiptDate } : {}) },
  });
  return response.data;
};

export const createPurchaseOrder = async (data) => {
  const response = await api.post(`${API_BASE}/api/procurement/purchase-orders`, data);
  return response.data;
};

export const updatePurchaseOrder = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/procurement/purchase-orders/${id}`, data);
  return response.data;
};

export const deletePurchaseOrder = async (id) => {
  const response = await api.delete(`${API_BASE}/api/procurement/purchase-orders/${id}`);
  return response.data;
};

// PO-level payments (nested under purchase-orders/:id/payments)
export const listPoPayments = async (poId) => {
  const r = await api.get(`${API_BASE}/api/procurement/purchase-orders/${poId}/payments`);
  return r.data;
};
export const createPoPayment = async (poId, data) => {
  const r = await api.post(`${API_BASE}/api/procurement/purchase-orders/${poId}/payments`, data);
  return r.data;
};
export const updatePoPayment = async (poId, paymentId, data) => {
  const r = await api.put(`${API_BASE}/api/procurement/purchase-orders/${poId}/payments/${paymentId}`, data);
  return r.data;
};
export const deletePoPayment = async (poId, paymentId) => {
  const r = await api.delete(`${API_BASE}/api/procurement/purchase-orders/${poId}/payments/${paymentId}`);
  return r.data;
};

export const submitPurchaseOrder = async (id) => {
  const response = await api.post(`${API_BASE}/api/procurement/purchase-orders/${id}/submit`);
  return response.data;
};

export const approvePurchaseOrder = async (id, approvedBy) => {
  const response = await api.post(`${API_BASE}/api/procurement/purchase-orders/${id}/approve`, { approved_by: approvedBy });
  return response.data;
};

export const sendPurchaseOrder = async (id, data = {}) => {
  const response = await api.post(`${API_BASE}/api/procurement/purchase-orders/${id}/send`, data);
  return response.data;
};

export const cancelPurchaseOrder = async (id, reason) => {
  const response = await api.post(`${API_BASE}/api/procurement/purchase-orders/${id}/cancel`, { cancellation_reason: reason });
  return response.data;
};

export const closePurchaseOrder = async (id) => {
  const response = await api.post(`${API_BASE}/api/procurement/purchase-orders/${id}/close`);
  return response.data;
};

export const getPurchaseOrderHistory = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/purchase-orders/${id}/history`);
  return response.data;
};

export const updateDeliveryDates = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/procurement/purchase-orders/${id}/delivery`, data);
  return response.data;
};

// ============================================================================
// SHIPMENTS (PO landed-cost / inbound logistics layer)
// ============================================================================
// Tier 1 (read-only). Tier 2 will add POST/PUT/DELETE write endpoints.

// List all shipments for a PO (ordered by sequence_no). Headers only — call
// getShipment(id) for the full record with allocated line costs.
export const getShipmentsForPO = async (poId) => {
  const response = await api.get(`${API_BASE}/api/procurement/shipments/by-po/${poId}`);
  return response.data;
};

// Single shipment with its lines, allocated per-line costs and landed_unit_cost.
export const getShipment = async (shipmentId) => {
  const response = await api.get(`${API_BASE}/api/procurement/shipments/${shipmentId}`);
  return response.data;
};

// Recompute allocation (manual refresh — typically only needed if header
// freight/customs were edited via the legacy PO header path).
export const recomputeShipmentAllocation = async (shipmentId) => {
  const response = await api.post(`${API_BASE}/api/procurement/shipments/${shipmentId}/recompute-allocation`);
  return response.data;
};

// Tier 2 writes
// Create a new shipment for a PO by SPLITTING qty from the default shipment.
// Body: { po_id, ...header fields, lines: [{po_item_id, quantity_shipped}, ...] }
export const createShipment = async (payload) => {
  const response = await api.post(`${API_BASE}/api/procurement/shipments`, payload);
  return response.data;
};

// Edit shipment header (freight/customs/insurance/other/dates/transport/etc.).
// Allocation is recomputed server-side after the update.
export const updateShipment = async (shipmentId, payload) => {
  const response = await api.put(`${API_BASE}/api/procurement/shipments/${shipmentId}`, payload);
  return response.data;
};

// Soft-delete a non-default shipment; line qtys move back to the default
// shipment so the PO's total ordered qty stays whole.
export const deleteShipment = async (shipmentId) => {
  const response = await api.delete(`${API_BASE}/api/procurement/shipments/${shipmentId}`);
  return response.data;
};

// Two-stage receipt: dispatch event.
// Captures BL, carrier, ETD/ETA, incoterms, ports + optional freight/customs,
// posts Dr 1220 GIT / Cr 2150 GRNI JE, flips shipment.status='shipped' + PO.status='in_transit'.
export const markShipmentDispatched = async (shipmentId, payload = {}) => {
  const response = await api.post(`${API_BASE}/api/procurement/shipments/${shipmentId}/mark-dispatched`, payload);
  return response.data;
};
export const markShipmentArrived = async (shipmentId, payload = {}) => {
  const response = await api.post(`${API_BASE}/api/procurement/shipments/${shipmentId}/mark-arrived`, payload);
  return response.data;
};
export const cancelShipmentDispatch = async (shipmentId, reason = '') => {
  const response = await api.post(`${API_BASE}/api/procurement/shipments/${shipmentId}/cancel-dispatch`, { reason });
  return response.data;
};
// Document upload — multipart form. docType ∈ {supplier_ci, packing_list, bill_of_lading}.
export const uploadShipmentDocument = async (shipmentId, docType, file) => {
  const fd = new FormData();
  fd.append('file', file);
  const response = await api.post(
    `${API_BASE}/api/procurement/shipments/${shipmentId}/documents/${docType}`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};
export const deleteShipmentDocument = async (shipmentId, docType) => {
  const response = await api.delete(`${API_BASE}/api/procurement/shipments/${shipmentId}/documents/${docType}`);
  return response.data;
};
export const shipmentDocumentUrl = (shipmentId, docType) =>
  `${API_BASE}/api/procurement/shipments/${shipmentId}/documents/${docType}`;

// ============================================================================
// GOODS RECEIPTS
// ============================================================================

export const getGoodsReceipts = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/goods-receipts`, { params });
  return response.data;
};

export const getGoodsReceipt = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/goods-receipts/${id}`);
  return response.data;
};

export const createGoodsReceipt = async (data) => {
  const response = await api.post(`${API_BASE}/api/procurement/goods-receipts`, data);
  return response.data;
};

// Multi-destination variant — server groups items by per-line warehouse_code
// and creates N Pattern-A GRs (one per destination). Falls back to a single
// GR server-side when all items resolve to the same warehouse. Frontend
// callers can always use this when item-level overrides may exist; the
// fast-path on the server makes it equivalent to createGoodsReceipt otherwise.
export const createGoodsReceiptsSplit = async (data) => {
  const response = await api.post(`${API_BASE}/api/procurement/goods-receipts/split`, data);
  return response.data;
};

export const updateQualityInspection = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/procurement/goods-receipts/${id}/quality`, data);
  return response.data;
};

export const acceptGoodsReceipt = async (id) => {
  const response = await api.post(`${API_BASE}/api/procurement/goods-receipts/${id}/accept`);
  return response.data;
};

export const rejectGoodsReceipt = async (id, reason) => {
  const response = await api.post(`${API_BASE}/api/procurement/goods-receipts/${id}/reject`, { rejection_reason: reason });
  return response.data;
};

// Edit GR line quantities (received / accepted / rejected per line) after
// the receipt is already created. Backend enforces over-receipt + invariant
// guards then recomputes the parent PO's totals + status.
export const updateGoodsReceipt = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/procurement/goods-receipts/${id}`, data);
  return response.data;
};

// Add/correct batch number and/or expiry on recorded receipt lines. data = { items:
// [{ gr_item_id, batch_number?, expiry_date? }] }. Backend writes goods_receipt_items
// + inventory_batches; expiry editable anytime, batch number refused (409) if that
// batch's stock has moved.
export const updateGoodsReceiptBatchExpiry = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/procurement/goods-receipts/${id}/batch-expiry`, data);
  return response.data;
};

// Hard-delete a goods receipt. Backend rolls back the PO's received_quantity
// from this GR's contribution and recomputes PO status. Audit-logged.
export const deleteGoodsReceipt = async (id) => {
  const response = await api.delete(`${API_BASE}/api/procurement/goods-receipts/${id}`);
  return response.data;
};

// ============================================================================
// IMPORTED RECEIPT REVIEW / CLEANUP
// ============================================================================

export const getImportedReceipts = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/imported-receipts`, { params });
  return response.data;
};

export const getImportedReceipt = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/imported-receipts/${id}`);
  return response.data;
};

export const getImportedReceiptPoSuggestions = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/imported-receipts/${id}/po-suggestions`);
  return response.data;
};

export const approveImportedReceiptDraft = async (id, data) => {
  const response = await api.post(`${API_BASE}/api/procurement/imported-receipts/${id}/approve`, data);
  return response.data;
};

export const voidImportedReceipt = async (id, reason) => {
  const response = await api.post(`${API_BASE}/api/procurement/imported-receipts/${id}/void`, { reason });
  return response.data;
};

// Document upload/delete for GR header — docType is 'commercial_invoice' or 'packing_list'.
// File-size limit 25 MB, PDF/PNG/JPG only (enforced server-side).
export const uploadGoodsReceiptDocument = async (id, docType, file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await api.post(
    `${API_BASE}/api/procurement/goods-receipts/${id}/documents/${docType}`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export const deleteGoodsReceiptDocument = async (id, docType) => {
  const response = await api.delete(`${API_BASE}/api/procurement/goods-receipts/${id}/documents/${docType}`);
  return response.data;
};

// Activity timeline for a GR — audit_log entries enriched with user names + humanized actions.
export const getGoodsReceiptTimeline = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/goods-receipts/${id}/timeline`);
  return response.data;
};

// ============================================================================
// INVOICES
// ============================================================================

export const getInvoices = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/invoices`, { params });
  return response.data;
};

export const getInvoice = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/invoices/${id}`);
  return response.data;
};

export const createInvoice = async (data) => {
  const response = await api.post(`${API_BASE}/api/procurement/invoices`, data);
  return response.data;
};

export const updateInvoice = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/procurement/invoices/${id}`, data);
  return response.data;
};

// Attach a PO to an EXISTING invoice (pass poId = null to detach). Works regardless of
// invoice status — this is how QuickBooks-imported bills (status='approved', po_id NULL)
// get their PO Reference. Backend rejects a PO belonging to a different supplier.
export const linkInvoicePo = async (id, poId) => {
  const response = await api.post(`${API_BASE}/api/procurement/invoices/${id}/link-po`, { po_id: poId ?? null });
  return response.data;
};

export const getPendingMatchingInvoices = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/invoices/pending-matching`, { params });
  return response.data;
};

export const performThreeWayMatching = async (id, tolerancePercentage = 5) => {
  const response = await api.post(`${API_BASE}/api/procurement/invoices/${id}/match`, { tolerance_percentage: tolerancePercentage });
  return response.data;
};

export const approveInvoice = async (id, approvedBy) => {
  const response = await api.post(`${API_BASE}/api/procurement/invoices/${id}/approve`, { approved_by: approvedBy });
  return response.data;
};

export const rejectInvoice = async (id, reason) => {
  const response = await api.post(`${API_BASE}/api/procurement/invoices/${id}/reject`, { rejection_reason: reason });
  return response.data;
};

// ============================================================================
// PAYMENTS
// ============================================================================

export const getPayments = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/payments`, { params });
  return response.data;
};

export const getPayment = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/payments/${id}`);
  return response.data;
};

export const recordPayment = async (data) => {
  const response = await api.post(`${API_BASE}/api/procurement/payments`, data);
  return response.data;
};

export const createBatchPayment = async (payments) => {
  const response = await api.post(`${API_BASE}/api/procurement/payments/batch`, { payments });
  return response.data;
};

export const getAgingReport = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/payments/aging`, { params });
  return response.data;
};

// ============================================================================
// REPORTS
// ============================================================================

export const getSpendAnalysis = async (params) => {
  const response = await api.get(`${API_BASE}/api/procurement/reports/spend-analysis`, { params });
  return response.data;
};

export const getSupplierScorecards = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/reports/supplier-scorecards`, { params });
  return response.data;
};

export const getInventoryForecast = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/reports/inventory-forecast`, { params });
  return response.data;
};

export const getCashFlowForecast = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/reports/cash-flow-forecast`, { params });
  return response.data;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format currency value
 */
export const formatCurrency = (value, currency = 'GBP') => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency
  }).format(value || 0);
};

/**
 * Format number with commas
 */
export const formatNumber = (value) => {
  return new Intl.NumberFormat('en-GB').format(value || 0);
};

/**
 * Format date for display (YYYY-MM-DD format)
 */
export const formatDate = (dateString) => {
  if (!dateString || dateString === '1970-01-01 00:00:00') return 'N/A';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get status color for chips
 */
export const getStatusColor = (status) => {
  const statusColors = {
    // Purchase Order statuses
    draft: 'default',
    submitted: 'info',
    approved: 'success',
    sent: 'info',
    partially_received: 'warning',
    completed: 'success',
    cancelled: 'error',
    // Invoice statuses
    pending: 'warning',
    matched: 'info',
    paid: 'success',
    rejected: 'error',
    // Payment statuses
    partial: 'warning',
    overdue: 'error',
    // Active states
    active: 'success',
    inactive: 'default'
  };
  return statusColors[status?.toLowerCase()] || 'default';
};

/**
 * Calculate days until date
 */
export const daysUntil = (dateString) => {
  if (!dateString) return null;
  const target = new Date(dateString);
  const today = new Date();
  const diffTime = target - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Get supplier type label
 */
export const getSupplierTypeLabel = (type) => {
  const labels = {
    product: 'Product Supplier',
    service: 'Service Provider',
    raw_material: 'Raw Material',
    packaging: 'Packaging',
    employee: 'Employee (payroll)',
    contractor: 'Contractor (monthly)'
  };
  return labels[type] || type;
};

/**
 * Get payment method label
 */
export const getPaymentMethodLabel = (method) => {
  const labels = {
    bank_transfer: 'Bank Transfer',
    check: 'Check',
    cash: 'Cash',
    credit_card: 'Credit Card'
  };
  return labels[method] || method;
};

export default {
  // Suppliers
  getSuppliers,
  getSupplier,
  createSupplier,
  updateSupplier,
  deactivateSupplier,
  deleteSupplierPermanent,
  getSupplierPerformance,
  addSupplierProduct,
  updateSupplierProduct,
  // Purchase Orders
  getPurchaseOrders,
  getPurchaseOrder,
  createPurchaseOrder,
  updatePurchaseOrder,
  deletePurchaseOrder,
  submitPurchaseOrder,
  approvePurchaseOrder,
  sendPurchaseOrder,
  closePurchaseOrder,
  cancelPurchaseOrder,
  getPurchaseOrderHistory,
  updateDeliveryDates,
  // Goods Receipts
  getGoodsReceipts,
  getGoodsReceipt,
  createGoodsReceipt,
  createGoodsReceiptsSplit,
  updateQualityInspection,
  acceptGoodsReceipt,
  rejectGoodsReceipt,
  updateGoodsReceipt,
  deleteGoodsReceipt,
  // Invoices
  getInvoices,
  getInvoice,
  createInvoice,
  updateInvoice,
  linkInvoicePo,
  getPendingMatchingInvoices,
  performThreeWayMatching,
  approveInvoice,
  rejectInvoice,
  // Payments
  getPayments,
  getPayment,
  recordPayment,
  createBatchPayment,
  getAgingReport,
  // Reports
  getSpendAnalysis,
  getSupplierScorecards,
  getInventoryForecast,
  getCashFlowForecast,
  // Helpers
  formatCurrency,
  formatNumber,
  formatDate,
  getStatusColor,
  daysUntil,
  getSupplierTypeLabel,
  getPaymentMethodLabel
};
